import React, { useRef, useEffect, useCallback } from 'react';

/**
 * Custom checkbox renderer for AG Grid v33+.
 *
 * Performance note: uses direct DOM mutation instead of React state.
 * AG Grid fires `rowSelected` on every visible node when any row is
 * clicked (via forEachNode → setSelected). With useState that would
 * cause 20+ React re-renders per click for a 20-row page. Instead we
 * toggle the ant-checkbox-checked class and the <input> checked
 * property imperatively — zero React reconciliation on selection change.
 */
const CheckboxSelectionRender = (props) => {
  const spanRef = useRef(null);

  const updateDOM = useCallback((checked) => {
    const span = spanRef.current;
    if (!span) return;
    span.classList.toggle('ant-checkbox-checked', checked);
    const input = span.querySelector('input');
    if (input) input.checked = checked;
  }, []);

  useEffect(() => {
    // Sync on mount — node may already be selected (e.g. returning to a page).
    updateDOM(!!props.node.isSelected());

    const onRowSelected = () => updateDOM(!!props.node.isSelected());
    props.node.addEventListener('rowSelected', onRowSelected);
    return () => props.node.removeEventListener('rowSelected', onRowSelected);
  }, [props.node, updateDOM]);

  const initialChecked = !!props.node.isSelected();

  // Replicate Ant Design's checkbox DOM structure directly so we can drive
  // it imperatively.  The ant-checkbox-checked class controls the visual
  // checked state; the hidden <input> keeps the native checked property in
  // sync so that browser focus / accessibility still works.
  // The outer div fills the cell and uses flex to centre the checkbox both
  // horizontally and vertically (the old <Checkbox> wrapper did this via the
  // ant-checkbox-wrapper label styles; bare span.ant-checkbox is inline and
  // would otherwise sit at the top-left of the cell).
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <span
        ref={spanRef}
        className={`ant-checkbox${initialChecked ? ' ant-checkbox-checked' : ''}`}
      >
        <input
          type='checkbox'
          className='ant-checkbox-input'
          name='ag-grid-input-check-clicked'
          defaultChecked={initialChecked}
          readOnly
        />
        <span className='ant-checkbox-inner' />
      </span>
    </div>
  );
};

export default CheckboxSelectionRender;
